import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X, Search, Briefcase, UserRound, Plug, Building2, GitBranch,
} from 'lucide-react';
import JobFamilyPanel, { JobFamilyInfoButton } from './JobFamilyPanel';
import { setJobFamilyAiEnabled } from '../../data/jobFamilyMaster';
import { isAiJdEnabled } from '../../utils/entitlements';
import { useAuth } from '../../context/AuthContext';
import { openWizetokSso, userEmailFromAuth, getWizetokBridgeUrl } from '../../utils/crossAppSso';

const MENU_ITEMS = [
  { id: 'job-family', label: 'Job Family', icon: Briefcase },
  { id: 'recruiter-alias', label: 'Recruiter Alias', icon: UserRound },
  { id: 'connectors', label: 'Connectors', icon: Plug },
  { id: 'organization-settings', label: 'Organization Settings', icon: Building2 },
  { id: 'workflow', label: 'Workflow', icon: GitBranch },
];

const SECTION_TITLES = {
  'job-family': 'Job Family',
  'recruiter-alias': 'Recruiter Alias',
  connectors: 'Connectors',
  'organization-settings': 'Organization Settings',
  workflow: 'Workflow',
};

const SECTION_DESCRIPTIONS = {
  'job-family': 'Configure skill weightage per designation here. Stack Ranking and new positions use these profiles automatically.',
  'recruiter-alias': 'Manage recruiter display names and email aliases.',
  connectors: 'Connect external tools and data sources.',
  'organization-settings': 'Manage organization-wide preferences and defaults.',
};

const WORKFLOW_LINE_GRADIENT = 'linear-gradient(90deg, #e5e7eb 0%, #e5e7eb 14%, #4a90e2 26%, #8b5cf6 42%, #8b5cf6 58%, #a78bfa 74%, #e5e7eb 86%, #e5e7eb 100%)';

const WORKFLOW_STEPS = [
  {
    title: 'Create JD',
    description: 'Structured JD in 60s',
    tag: 'JD Craft',
    emoji: '✍️',
    path: '/open-positions',
  },
  {
    title: 'Rank & Engage',
    description: 'AI-scored shortlist',
    tag: 'ReachOut',
    emoji: '🔍',
    path: '/engagement',
  },
  {
    title: 'Assess Skills',
    description: 'Auto tech tests',
    tag: 'CodeSense',
    emoji: '💻',
    href: 'https://codesense.hirecrux.ai',
  },
  {
    title: 'Interview',
    description: 'AI-led conversations',
    tag: 'WizeTok',
    emoji: '🎙️',
    app: 'wizetok',
  },
  {
    title: 'Hire & Onboard',
    description: 'Stack rank, close fast',
    tag: 'Stack Ranking',
    emoji: '🤝',
    path: '/stack-ranking',
  },
];

function WorkflowFlow({ onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStepClick = (step) => {
    if (!step.path && !step.href && step.app !== 'wizetok') return;

    onClose();

    if (step.path) {
      navigate(step.path, { state: { _refresh: Date.now() } });
      return;
    }

    if (step.app === 'wizetok') {
      const email = userEmailFromAuth(user);
      void openWizetokSso(email);
      return;
    }

    window.location.assign(step.href);
  };

  return (
    <div className="settings-workflow">
      <div className="settings-workflow-hero">
        <span className="settings-workflow-badge">
          <span className="settings-workflow-badge-dot" aria-hidden="true" />
          Workflow
        </span>
        <h3 className="settings-workflow-heading">JD to offer. One connected flow.</h3>
        <p className="settings-workflow-subheading">
          Each agent feeds the next — zero manual handoffs, zero coordination overhead.
        </p>
      </div>

      <div className="settings-workflow-track" role="list">
        <div
          className="settings-workflow-line"
          style={{ background: WORKFLOW_LINE_GRADIENT }}
          aria-hidden="true"
        />
        {WORKFLOW_STEPS.map((step) => (
            <div key={step.tag} className="settings-workflow-step" role="listitem">
              <button
                type="button"
                className="settings-workflow-step-btn"
                onClick={() => handleStepClick(step)}
                aria-label={`${step.title}: ${step.tag}`}
                title={step.app === 'wizetok' ? `Open Wizetok (${getWizetokBridgeUrl()})` : undefined}
              >
                <div className="settings-workflow-icon-wrap">
                  <span className="settings-workflow-emoji" aria-hidden="true">{step.emoji}</span>
                </div>
                <h4 className="settings-workflow-step-title">{step.title}</h4>
                <p className="settings-workflow-step-desc">{step.description}</p>
                <span className="settings-workflow-tag">{step.tag}</span>
              </button>
            </div>
        ))}
      </div>
    </div>
  );
}

// Job Family settings belong to Craft JD, so its AI suggestions follow the Craft JD switch.
function JobFamilyPanelWithAiSwitch() {
  const { entitlements, unrestricted } = useAuth();
  setJobFamilyAiEnabled(isAiJdEnabled(entitlements, { unrestricted }));
  return <JobFamilyPanel />;
}

function SettingsSectionContent({ sectionId, onClose }) {
  if (sectionId === 'workflow') {
    return <WorkflowFlow onClose={onClose} />;
  }

  if (sectionId === 'job-family') {
    return <JobFamilyPanelWithAiSwitch />;
  }

  const activeItem = MENU_ITEMS.find((m) => m.id === sectionId);
  const Icon = activeItem?.icon || Briefcase;

  return (
    <div className="settings-modal-placeholder">
      <div className="settings-modal-placeholder-icon" aria-hidden="true">
        <Icon size={28} strokeWidth={1.5} />
      </div>
    </div>
  );
}

export default function SettingsModal({ show, onClose, initialSectionId = 'job-family' }) {
  const [selectedId, setSelectedId] = useState(initialSectionId);
  const [searchQuery, setSearchQuery] = useState('');
  const [mounted, setMounted] = useState(show);
  const [animating, setAnimating] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (show && initialSectionId) {
      setSelectedId(initialSectionId);
    }
  }, [show, initialSectionId]);

  useEffect(() => {
    if (show) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setAnimating(true));
      document.body.style.overflow = 'hidden';
      return () => cancelAnimationFrame(frame);
    }

    setAnimating(false);
    const timer = window.setTimeout(() => setMounted(false), 220);
    document.body.style.overflow = '';
    return () => window.clearTimeout(timer);
  }, [show]);

  useEffect(() => () => {
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [show, onClose]);

  useEffect(() => {
    if (show && searchRef.current) {
      searchRef.current.focus();
    }
  }, [show]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return MENU_ITEMS;
    return MENU_ITEMS.filter((item) => item.label.toLowerCase().includes(q));
  }, [searchQuery]);

  const selectedTitle = SECTION_TITLES[selectedId] || SECTION_TITLES['job-family'];

  if (!mounted) return null;

  return createPortal(
    <div
      className="settings-modal-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        paddingLeft: 'calc(var(--app-sidebar-width, 0px) + 16px)',
        fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Backdrop */}
      <div
        className={`settings-modal-backdrop${animating ? ' settings-modal-backdrop--open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        className={`settings-modal-dialog${animating ? ' settings-modal-dialog--open' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left sidebar */}
        <aside className="settings-modal-sidebar">
          <div className="settings-modal-search-wrap">
            <Search size={15} className="settings-modal-search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="settings-modal-search"
              aria-label="Search settings"
            />
          </div>

          <div className="settings-modal-nav-label">Settings</div>

          <nav className="settings-modal-nav" aria-label="Settings sections">
            {filteredItems.length === 0 ? (
              <div className="settings-modal-nav-empty">No results</div>
            ) : (
              filteredItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.id === selectedId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`settings-modal-nav-item${isActive ? ' settings-modal-nav-item--active' : ''}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
                    <span>{item.label}</span>
                  </button>
                );
              })
            )}
          </nav>
        </aside>

        {/* Right content */}
        <section className="settings-modal-content">
          <div className="settings-modal-top-actions">
            <button
              type="button"
              className="settings-modal-close"
              onClick={onClose}
              aria-label="Close settings"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>

          <div className="settings-modal-content-inner">
            {selectedId !== 'workflow' && (
              <>
                {selectedId === 'job-family' ? (
                  <div className="settings-modal-title-row">
                    <h2 id="settings-modal-title" className="settings-modal-title">
                      {selectedTitle}
                    </h2>
                    <JobFamilyInfoButton />
                  </div>
                ) : (
                  <h2 id="settings-modal-title" className="settings-modal-title">
                    {selectedTitle}
                  </h2>
                )}
                {SECTION_DESCRIPTIONS[selectedId] && (
                  <p className="settings-modal-description">{SECTION_DESCRIPTIONS[selectedId]}</p>
                )}
              </>
            )}

            {selectedId === 'workflow' && (
              <h2 id="settings-modal-title" className="settings-modal-title settings-modal-title--sr">
                {selectedTitle}
              </h2>
            )}

            <SettingsSectionContent sectionId={selectedId} onClose={onClose} />
          </div>
        </section>
      </div>
    </div>,
    document.body
  );
}
